import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Button from "@/components/Base/Button";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { Menu } from "@/components/Base/Headless";
import axios from "axios";

type ProductGroup = { id: string; groupName: string; companyId?: string };
type Product = {
  id: string;
  name: string;
  sku?: string;
  type?: string;
  group?: string;
  status: boolean;
  retailSellingPrice?: number | null;
  dealerSellingPrice?: number | null;
};
type ProductGroupPriceConfig = { groupName: string; priceType?: "DEALER" | "RETAIL" | string };
type Team = { id: string; name: string };
type Distributor = { id: string; name: string; supplyTo?: string[] };

const STORAGE_KEY_FILTER = "stockledger_stockOutAdd_filter";
const STORAGE_KEY_SORT = "stockledger_stockOutAdd_sort";

const loadSavedFilter = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FILTER);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
};

const loadSavedSortOrder = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SORT);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string") ? parsed : [];
  } catch {
    return [];
  }
};

export default function AddStockOutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get("companyId");

  const [teamId, setTeamId] = useState("");
  const [outAt, setOutAt] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [refNo, setRefNo] = useState("");
  const [loadingRefNo, setLoadingRefNo] = useState(false);

  const [teams, setTeams] = useState<Team[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [priceTypeByGroupName, setPriceTypeByGroupName] = useState<Record<string, "DEALER" | "RETAIL">>({});
  const [productBalances, setProductBalances] = useState<Record<string, number>>({});
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [returnedQuantities, setReturnedQuantities] = useState<Record<string, number>>({});
  const [returnedInputEnabled, setReturnedInputEnabled] = useState<Record<string, boolean>>({});
  const [expandedTypeIds, setExpandedTypeIds] = useState<Set<string>>(new Set());

  const [selectedProductTypeFilters, setSelectedProductTypeFilters] = useState<string[]>(loadSavedFilter);
  const [productTypeCustomOrder, setProductTypeCustomOrder] = useState<string[]>(loadSavedSortOrder);

  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();
  const queryCompany = companyIdFromUrl ? `?companyId=${companyIdFromUrl}` : "";

  useEffect(() => {
    (async () => {
      try {
        setLoadingTeams(true);
        const [teamsRes, distRes] = await Promise.all([
          axios.get(`${apiUrl}/teams?pageSize=500${companyIdFromUrl ? `&companyId=${companyIdFromUrl}` : ""}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiUrl}/distributors?pageSize=500${companyIdFromUrl ? `&companyId=${companyIdFromUrl}` : ""}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const teamsData = teamsRes.data?.data ?? [];
        setTeams(Array.isArray(teamsData) ? teamsData : []);
        const distData = distRes.data?.data ?? [];
        setDistributors(Array.isArray(distData) ? distData : []);
      } catch (err) {
        console.error("Failed to fetch teams / distributors", err);
      } finally {
        setLoadingTeams(false);
      }
    })();
  }, [apiUrl, token, companyIdFromUrl]);

  /** Team names that at least one distributor supplies to (Distributor.supplyTo). Only these teams are valid for Stock OUT. */
  const suppliedTeamNames = useMemo(() => {
    const set = new Set<string>();
    distributors.forEach((d) => {
      (d.supplyTo || []).forEach((name) => {
        if (name && String(name).trim()) set.add(String(name).trim());
      });
    });
    return set;
  }, [distributors]);

  /** Teams that are assigned to at least one distributor (supplyTo contains team name). */
  const teamsAllowedByDistributor = useMemo(() => {
    return teams.filter((t) => suppliedTeamNames.has((t.name || "").trim()));
  }, [teams, suppliedTeamNames]);

  /** For the selected team, which distributor(s) supply it (for display). */
  const distributorsSupplyingSelectedTeam = useMemo(() => {
    if (!teamId.trim()) return [];
    const team = teams.find((t) => t.id === teamId);
    if (!team?.name) return [];
    const name = (team.name || "").trim();
    return distributors.filter((d) => (d.supplyTo || []).some((n) => String(n).trim() === name));
  }, [teamId, teams, distributors]);

  useEffect(() => {
    if (!teamId) return;
    const allowed = teamsAllowedByDistributor.some((t) => t.id === teamId);
    if (!allowed) setTeamId("");
  }, [teamId, teamsAllowedByDistributor]);

  useEffect(() => {
    if (!teamId.trim()) {
      setRefNo("");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingRefNo(true);
      try {
        const res = await axios.get(`${apiUrl}/stock-out/next-batch-no${queryCompany}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data?.batchNo) setRefNo(String(res.data.batchNo));
      } catch {
        if (!cancelled) setRefNo("");
      } finally {
        if (!cancelled) setLoadingRefNo(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, token, queryCompany, teamId]);

  useEffect(() => {
    (async () => {
      if (!teamId.trim()) {
        setProductBalances({});
        return;
      }
      try {
        const params = new URLSearchParams();
        params.set("teamId", teamId.trim());
        if (companyIdFromUrl) params.set("companyId", companyIdFromUrl);
        const res = await axios.get(`${apiUrl}/stock-out/opening-balances?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (Array.isArray(res.data)) {
          const map: Record<string, number> = {};
          res.data.forEach((row: { productId: string; balance: number }) => {
            map[row.productId] = row.balance;
          });
          setProductBalances(map);
        } else {
          setProductBalances({});
        }
      } catch (err) {
        console.error("Failed to load team opening balances", err);
        setProductBalances({});
      }
    })();
  }, [apiUrl, token, companyIdFromUrl, teamId]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingData(true);
        const params = new URLSearchParams();
        params.set("pageSize", "500");
        if (companyIdFromUrl) params.set("companyId", companyIdFromUrl);
        const typesParams = new URLSearchParams();
        typesParams.set("pageSize", "200");
        if (companyIdFromUrl) typesParams.set("companyId", companyIdFromUrl);
        const [productsRes, groupsRes] = await Promise.all([
          axios.get(`${apiUrl}/products?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/product-groups?${typesParams.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setProductGroups(groupsRes.data?.data ?? []);
        const list = productsRes.data?.data ?? [];
        const fromProductTable = Array.isArray(list) ? list : [];
        setProducts(fromProductTable.filter((p: Product) => p.status !== false));
        const groups = groupsRes.data?.data ?? [];
        const nextMap: Record<string, "DEALER" | "RETAIL"> = {};
        (Array.isArray(groups) ? groups : []).forEach((g: ProductGroupPriceConfig) => {
          const key = (g.groupName || "").trim().toLowerCase();
          if (!key) return;
          nextMap[key] = g.priceType === "DEALER" ? "DEALER" : "RETAIL";
        });
        setPriceTypeByGroupName(nextMap);
      } catch (err) {
        console.error("Failed to load product groups / products", err);
      } finally {
        setLoadingData(false);
      }
    })();
  }, [apiUrl, companyIdFromUrl, token]);

  const productsByType = useMemo(() => {
    const map = new Map<string, { label: string; products: Product[] }>();
    for (const pg of productGroups) {
      const list = products.filter((p) => (p.group || "").trim() === (pg.groupName || "").trim());
      if (list.length > 0) map.set(pg.id, { label: pg.groupName, products: list });
    }
    const other = products.filter((p) => {
      const g = (p.group || "").trim();
      return !productGroups.some((pg) => (pg.groupName || "").trim() === g);
    });
    if (other.length > 0) map.set("__other__", { label: "Other", products: other });
    return map;
  }, [productGroups, products]);

  const orderedProductTypes = useMemo(() => {
    return Array.from(productsByType.entries());
  }, [productsByType]);

  const filteredAndSortedProductTypes = useMemo(() => {
    let list = orderedProductTypes;
    if (selectedProductTypeFilters.length > 0) {
      const set = new Set(selectedProductTypeFilters.map((s) => s.trim().toLowerCase()));
      list = list.filter(([, v]) => set.has(v.label.trim().toLowerCase()));
    }
    if (productTypeCustomOrder.length > 0) {
      const orderMap = new Map(productTypeCustomOrder.map((label, i) => [label.trim().toLowerCase(), i]));
      list = [...list].sort((a, b) => {
        const ia = orderMap.get(a[1].label.trim().toLowerCase()) ?? 1e9;
        const ib = orderMap.get(b[1].label.trim().toLowerCase()) ?? 1e9;
        return ia - ib;
      });
    }
    return list;
  }, [orderedProductTypes, selectedProductTypeFilters, productTypeCustomOrder]);

  const sortMenuOrder = useMemo(() => {
    if (productTypeCustomOrder.length > 0) {
      const inCustom = productTypeCustomOrder.filter((label) =>
        orderedProductTypes.some(([, v]) => v.label.trim().toLowerCase() === label.trim().toLowerCase())
      );
      const rest = orderedProductTypes
        .filter(([, v]) => !productTypeCustomOrder.some((c) => c.trim().toLowerCase() === v.label.trim().toLowerCase()))
        .map(([, v]) => v.label);
      return [...inCustom, ...rest];
    }
    return orderedProductTypes.map(([, v]) => v.label);
  }, [orderedProductTypes, productTypeCustomOrder]);

  const moveSortOrder = useCallback((index: number, direction: "up" | "down") => {
    setProductTypeCustomOrder((prev) => {
      const next = [...sortMenuOrder];
      const j = direction === "up" ? index - 1 : index + 1;
      if (j < 0 || j >= next.length) return prev.length > 0 ? prev : [];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  }, [sortMenuOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTER, JSON.stringify(selectedProductTypeFilters));
    } catch {
      // ignore
    }
  }, [selectedProductTypeFilters]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SORT, JSON.stringify(productTypeCustomOrder));
    } catch {
      // ignore
    }
  }, [productTypeCustomOrder]);

  useEffect(() => {
    if (loadingData || productGroups.length === 0) return;
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      productsByType.forEach((_, id) => next.add(id));
      return next;
    });
  }, [loadingData, productsByType.size, productGroups.length]);

  const toggleType = useCallback((id: string) => {
    setExpandedTypeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setQuantity = useCallback((productId: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [productId]: value >= 0 ? value : 0 }));
  }, []);
  const setReturnedQuantity = useCallback((productId: string, value: number) => {
    setReturnedQuantities((prev) => ({ ...prev, [productId]: value >= 0 ? value : 0 }));
  }, []);
  const toggleReturnedInput = useCallback((productId: string) => {
    setReturnedInputEnabled((prev) => {
      const enabled = !!prev[productId];
      const next = { ...prev, [productId]: !enabled };
      if (enabled) {
        setReturnedQuantities((qPrev) => ({ ...qPrev, [productId]: 0 }));
      }
      return next;
    });
  }, []);

  const getConfiguredPriceType = useCallback(
    (product: Product): "DEALER" | "RETAIL" => {
      const key = (product.group || "").trim().toLowerCase();
      if (!key) return "RETAIL";
      return priceTypeByGroupName[key] || "RETAIL";
    },
    [priceTypeByGroupName]
  );

  const getPriceColumnLabel = useCallback(
    (prods: Product[]) => {
      const types = new Set(prods.map((p) => getConfiguredPriceType(p)));
      if (types.size === 1) {
        return Array.from(types)[0] === "DEALER" ? "Dealer Price" : "Retail Price";
      }
      return "Price";
    },
    [getConfiguredPriceType]
  );

  const getDisplayPrice = useCallback(
    (product: Product): number | null => {
      return getConfiguredPriceType(product) === "DEALER"
        ? product.dealerSellingPrice ?? null
        : product.retailSellingPrice ?? null;
    },
    [getConfiguredPriceType]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    if (!teamId.trim()) {
      setSubmitError("Please select a Team.");
      return;
    }
    const selectedTeam = teamsAllowedByDistributor.find((t) => t.id === teamId);
    if (!selectedTeam) {
      setSubmitError("The selected team is not assigned to any distributor. Please choose a team that is supplied by a distributor (see Distributors → Stocks Supply To).");
      return;
    }
    if (!outAt.trim()) {
      setSubmitError("Please select date.");
      return;
    }
    if (hasOverdrawn) {
      const first = overdrawnProductIds[0];
      const name = first ? (productById.get(first)?.name || "one or more products") : "one or more products";
      setSubmitError(`Net Stock OUT cannot exceed Available Stock. Please adjust Stock OUT / Stock RETURNED for ${name}.`);
      return;
    }
    const lines = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));
    const returnedLines = Object.entries(returnedQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));
    if (lines.length === 0 && returnedLines.length === 0) {
      setSubmitError("Please enter at least one Stock OUT or Stock RETURNED quantity.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${apiUrl}/stock-out${queryCompany}`,
        {
          teamId: teamId.trim(),
          outAt: (outAt.trim() ? new Date(outAt) : new Date()).toISOString(),
          lines,
          returnedLines,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitSuccess("Stock OUT saved successfully.");
      setQuantities({});
      setReturnedQuantities({});
      setReturnedInputEnabled({});
      try {
        const nextRes = await axios.get(`${apiUrl}/stock-out/next-batch-no${queryCompany}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (nextRes.data?.batchNo) setRefNo(String(nextRes.data.batchNo));
      } catch {
        // ignore
      }
      try {
        const balRes = await axios.get(`${apiUrl}/stock-out/product-balances${queryCompany}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (Array.isArray(balRes.data)) {
          const map: Record<string, number> = {};
          balRes.data.forEach((row: { productId: string; balance: number }) => {
            map[row.productId] = row.balance;
          });
          setProductBalances(map);
        }
      } catch {
        // ignore
      }
      setTimeout(() => {
        navigate(companyIdFromUrl ? `/stock-out?companyId=${companyIdFromUrl}` : "/stock-out");
      }, 2000);
    } catch (err: unknown) {
      const data = axios.isAxiosError(err) ? err.response?.data : undefined;
      const msg = data?.message ?? data?.error ?? "Failed to save";
      setSubmitError(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(companyIdFromUrl ? `/stock-out?companyId=${companyIdFromUrl}` : "/stock-out");
  };

  const handleReset = () => {
    setOutAt(dayjs().format("YYYY-MM-DD"));
    setQuantities({});
    setReturnedQuantities({});
    setReturnedInputEnabled({});
    setSubmitError("");
    setSubmitSuccess("");
    if (teamId.trim()) {
      axios.get(`${apiUrl}/stock-out/next-batch-no${queryCompany}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => { if (res.data?.batchNo) setRefNo(String(res.data.batchNo)); })
        .catch(() => setRefNo(""));
    }
  };

  const hasTeamSelected = !!teamId.trim();
  const productById = useMemo(() => {
    const m = new Map<string, Product>();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  const overdrawnProductIds = useMemo(() => {
    const ids: string[] = [];
    Object.entries(quantities).forEach(([productId, qty]) => {
      const opening = Number(productBalances[productId] ?? 0);
      const returned = Number(returnedQuantities[productId] ?? 0);
      const netOut = (qty ?? 0) - returned;
      if (netOut > opening) ids.push(productId);
    });
    return ids;
  }, [quantities, returnedQuantities, productBalances]);

  const hasOverdrawn = overdrawnProductIds.length > 0;

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <div className="col-span-12">
            <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Add Stock OUT</div>
              <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                <Button variant="outline-secondary" onClick={handleBack}>
                  <Lucide icon="ArrowLeft" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Back to list
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mt-3.5 flex flex-col gap-4">
              <div className="box p-4">
                <div className="flex flex-col gap-1 pb-3 border-b border-slate-200/60 dark:border-darkmode-400">
                  <div className="flex flex-nowrap items-center gap-4">
                    <span className="text-[0.94rem] font-medium shrink-0">Select Team</span>
                    <FormSelect
                      value={teamId}
                      onChange={(e) => setTeamId(e.target.value)}
                      className="w-[25%] max-w-[25%] min-w-0 rounded-[0.5rem]"
                      required
                    >
                      <option value="">Select...</option>
                      {teamsAllowedByDistributor.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </FormSelect>
                    {loadingTeams && <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-slate-500" />}
                  </div>
                  {!loadingTeams && teams.length > 0 && teamsAllowedByDistributor.length === 0 && (
                    <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                      No teams are assigned to any distributor. Configure Distributors (Stocks Supply To) first.
                    </p>
                  )}
                  {teamId && distributorsSupplyingSelectedTeam.length > 0 && (
                    <p className="text-slate-600 dark:text-slate-300 text-xs mt-1">
                      Supplied by: {distributorsSupplyingSelectedTeam.map((d) => d.name).join(", ")}
                    </p>
                  )}
                </div>
                {submitError && (
                  <div className="mt-3 flex items-center gap-2 rounded-[0.6rem] border border-danger/30 bg-danger/5 px-4 py-2.5 text-danger">
                    <Lucide icon="AlertTriangle" className="w-6 h-6 shrink-0" />
                    {submitError}
                  </div>
                )}
                {submitSuccess && (
                  <div className="mt-3 flex items-center gap-2 rounded-[0.6rem] border border-success/30 bg-success/5 px-4 py-2.5 text-success">
                    <Lucide icon="CheckCircle" className="w-6 h-6 shrink-0" />
                    {submitSuccess}
                  </div>
                )}

                {hasTeamSelected && (
                  <>
                    <div className="mt-3 flex flex-wrap items-end gap-4">
                      <div className="flex items-center gap-2">
                        <label className="shrink-0 text-sm">Date<span className="text-danger ml-0.5">*</span></label>
                        <div className="relative inline-flex">
                          <Lucide icon="Calendar" className="absolute group-[.mode--light]:!text-slate-800 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]" />
                          <Litepicker
                            value={outAt}
                            onChange={(e) => setOutAt(e.target.value)}
                            options={{
                              autoApply: true,
                              singleMode: true,
                              numberOfColumns: 1,
                              numberOfMonths: 1,
                              showWeekNumbers: false,
                              dropdowns: { minYear: 2020, maxYear: null, months: true, years: true },
                              format: "YYYY-MM-DD",
                            }}
                            className="w-full min-w-[140px] max-w-[160px] pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 shadow-sm group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-800 dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!border-darkmode-400 dark:!box text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="shrink-0 text-sm">Ref No.</label>
                        <FormInput
                          type="text"
                          readOnly
                          value={loadingRefNo ? "..." : refNo}
                          className="min-w-[140px] w-40 bg-slate-100 dark:bg-darkmode-700 cursor-default"
                          title="Auto-generated running number (batch no)"
                        />
                      </div>
                      <div className="flex gap-3 ml-auto shrink-0">
                        <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={submitting}>
                          Reset
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting || loadingData || hasOverdrawn} className="!bg-[#2D2926] hover:!bg-[#2D2926]/90 shrink-0">
                          {submitting && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                          Submit
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {hasTeamSelected && (
                <div className="box overflow-hidden">
                  {hasOverdrawn && (
                    <div className="mx-4 mt-4 mb-0 flex items-center gap-2 rounded-[0.6rem] border border-danger/30 bg-danger/5 px-4 py-2.5 text-danger">
                      <Lucide icon="AlertTriangle" className="w-5 h-5 shrink-0" />
                      Net Stock OUT cannot exceed Available Stock. Please fix highlighted rows.
                    </div>
                  )}
                  {loadingData ? (
                    <div className="p-6 flex justify-center">
                      <Lucide icon="Loader2" className="w-8 h-8 animate-spin text-slate-500" />
                    </div>
                  ) : orderedProductTypes.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">No products found. Add products and product types first.</div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-end gap-3 p-4 pb-3 border-b border-slate-200/60 dark:border-darkmode-400">
                        <Menu className="relative inline-block">
                          <Menu.Button className="relative inline-flex items-center min-w-[100px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                            <Lucide icon="Filter" className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                            Filter
                            {selectedProductTypeFilters.length > 0 && (
                              <span className="ml-1 text-xs">({selectedProductTypeFilters.length})</span>
                            )}
                          </Menu.Button>
                          <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                            <div className="p-4">
                              <div className="text-sm font-medium mb-3">Product Group</div>
                              <div className="flex items-center justify-between mb-2">
                                {orderedProductTypes.length > 0 && (
                                  <button
                                    type="button"
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                    onClick={() => {
                                      const allSelected = orderedProductTypes.every(([, v]) =>
                                        selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                                      );
                                      if (allSelected) {
                                        setSelectedProductTypeFilters([]);
                                      } else {
                                        setSelectedProductTypeFilters(orderedProductTypes.map(([, v]) => v.label));
                                      }
                                    }}
                                  >
                                    {orderedProductTypes.every(([, v]) =>
                                      selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === v.label.trim().toLowerCase())
                                    )
                                      ? "Deselect all"
                                      : "Select all"}
                                  </button>
                                )}
                              </div>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {orderedProductTypes.length === 0 ? (
                                  <div className="text-xs text-slate-400">No product groups available</div>
                                ) : (
                                  orderedProductTypes.map(([id, { label }]) => (
                                    <label key={id} className="flex items-center cursor-pointer gap-2">
                                      <input
                                        type="checkbox"
                                        className="form-check-input rounded border-slate-300"
                                        checked={selectedProductTypeFilters.some((s) => s.trim().toLowerCase() === label.trim().toLowerCase())}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedProductTypeFilters([...selectedProductTypeFilters, label]);
                                          } else {
                                            setSelectedProductTypeFilters(
                                              selectedProductTypeFilters.filter((s) => s.trim().toLowerCase() !== label.trim().toLowerCase())
                                            );
                                          }
                                        }}
                                      />
                                      <span className="text-xs">{label}</span>
                                    </label>
                                  ))
                                )}
                              </div>
                              {selectedProductTypeFilters.length > 0 && (
                                <button
                                  type="button"
                                  className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 hover:bg-slate-300 dark:hover:bg-darkmode-600 rounded"
                                  onClick={() => setSelectedProductTypeFilters([])}
                                >
                                  Clear Filters
                                </button>
                              )}
                            </div>
                          </Menu.Items>
                        </Menu>
                        <Menu className="relative inline-block">
                          <Menu.Button className="relative inline-flex items-center min-w-[100px] w-auto pl-9 pr-3 py-2 rounded-[0.5rem] border border-slate-300/60 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 text-slate-700 dark:text-slate-300 text-left text-sm whitespace-nowrap hover:bg-slate-50 dark:hover:bg-darkmode-700">
                            <Lucide icon="ArrowUpDown" className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500" />
                            Sort
                            {productTypeCustomOrder.length > 0 && (
                              <span className="ml-1 text-xs">(Custom)</span>
                            )}
                          </Menu.Button>
                          <Menu.Items className="w-64 z-[100]" style={{ zIndex: 9999 }}>
                            <div className="p-4">
                              <div className="text-sm font-medium mb-3">Display order</div>
                              <div className="space-y-1 max-h-48 overflow-y-auto">
                                {sortMenuOrder.length === 0 ? (
                                  <div className="text-xs text-slate-400">No product types available</div>
                                ) : (
                                  sortMenuOrder.map((label, index) => (
                                    <div
                                      key={label}
                                      className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-slate-100 dark:hover:bg-darkmode-600"
                                    >
                                      <span className="text-xs truncate flex-1">
                                        {index + 1}. {label}
                                      </span>
                                      <div className="flex items-center shrink-0 gap-0.5">
                                        <button
                                          type="button"
                                          className="p-1 rounded hover:bg-slate-200 dark:hover:bg-darkmode-500 disabled:opacity-40 disabled:pointer-events-none"
                                          title="Move up"
                                          disabled={index === 0}
                                          onClick={() => moveSortOrder(index, "up")}
                                        >
                                          <Lucide icon="ChevronUp" className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          className="p-1 rounded hover:bg-slate-200 dark:hover:bg-darkmode-500 disabled:opacity-40 disabled:pointer-events-none"
                                          title="Move down"
                                          disabled={index === sortMenuOrder.length - 1}
                                          onClick={() => moveSortOrder(index, "down")}
                                        >
                                          <Lucide icon="ChevronDown" className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                              {productTypeCustomOrder.length > 0 && (
                                <button
                                  type="button"
                                  className="w-full mt-3 px-3 py-1.5 text-xs bg-slate-200 dark:bg-darkmode-700 hover:bg-slate-300 dark:hover:bg-darkmode-600 rounded"
                                  onClick={() => setProductTypeCustomOrder([])}
                                >
                                  Reset to default order
                                </button>
                              )}
                            </div>
                          </Menu.Items>
                        </Menu>
                      </div>
                      {filteredAndSortedProductTypes.length === 0 ? (
                        <div className="p-6 text-center text-slate-500">No product groups match the filter.</div>
                      ) : (
                        filteredAndSortedProductTypes.map(([typeId, { label, products: prods }]) => {
                      const isExpanded = expandedTypeIds.has(typeId);
                      return (
                        <div key={typeId} className="border-t border-slate-200/60 dark:border-darkmode-400 first:border-t-0">
                          <button
                            type="button"
                            className="flex items-center gap-3 w-full text-left py-3 px-4 bg-slate-200 dark:bg-darkmode-600 hover:bg-slate-300/80 dark:hover:bg-darkmode-500 transition-colors"
                            onClick={() => toggleType(typeId)}
                            aria-expanded={isExpanded}
                          >
                            <div className="text-[0.94rem] font-medium">{label}</div>
                            <Lucide icon={isExpanded ? "ChevronUp" : "ChevronDown"} className="w-5 h-5 stroke-[1.3] ml-auto text-slate-500 shrink-0" />
                          </button>
                          {isExpanded && (
                            <div className="pl-12 pr-12 pb-4 pt-0 border-t border-slate-200/60 dark:border-darkmode-400">
                              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)] gap-x-2 gap-y-0 items-center py-1 border-b border-slate-200/40 dark:border-darkmode-400/60 text-xs font-medium text-slate-500">
                                <div>SKU Item</div>
                                <div>Product Name</div>
                                <div className="text-center">Available Stock</div>
                                <div className="text-center">{getPriceColumnLabel(prods)}</div>
                                <div className="text-center">Stock OUT</div>
                                <div className="text-center">Stock RETURNED</div>
                              </div>
                              {prods.map((p) => (
                                (() => {
                                  const opening = Number(productBalances[p.id] ?? 0);
                                  const outQty = Number(quantities[p.id] ?? 0);
                                  const isOverdrawn = outQty > opening;
                                  return (
                                <div
                                  key={p.id}
                                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,3fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)_minmax(0,2fr)] gap-x-2 gap-y-0 items-center py-1 border-b border-slate-200/40 dark:border-darkmode-400/60 last:border-b-0"
                                >
                                  <div className="text-sm truncate">{p.sku ?? "—"}</div>
                                  <div className="text-sm font-medium truncate">{p.name}</div>
                                  <div className="text-sm text-center text-slate-600 dark:text-slate-400 tabular-nums">
                                    {productBalances[p.id] != null
                                      ? (() => {
                                          const opening = Number(productBalances[p.id]) || 0;
                                          const used = Number(quantities[p.id] ?? 0) || 0;
                                          const returned = Number(returnedQuantities[p.id] ?? 0) || 0;
                                          const remaining = Math.max(opening - used + returned, 0);
                                          return remaining.toLocaleString();
                                        })()
                                      : "—"}
                                  </div>
                                  <div className="text-sm text-center text-slate-600 dark:text-slate-400 tabular-nums">
                                    {(() => {
                                      const displayPrice = getDisplayPrice(p);
                                      return displayPrice != null
                                        ? Number(displayPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        : "—";
                                    })()}
                                  </div>
                                  <div className="flex justify-center items-center">
                                    <FormInput
                                      type="number"
                                      min={0}
                                      step={1}
                                      placeholder="0"
                                      className={`w-full max-w-24 min-w-0 ${isOverdrawn ? "border-danger" : ""}`}
                                      value={quantities[p.id] ?? ""}
                                      onChange={(e) => setQuantity(p.id, parseInt(e.target.value, 10) || 0)}
                                    />
                                  </div>
                                  <div className="flex justify-center items-center">
                                    <div className="flex items-center gap-1 w-full max-w-32 min-w-0">
                                      <Button
                                        type="button"
                                        variant="outline-secondary"
                                        className="h-[34px] px-2 text-[10px] leading-none"
                                        onClick={() => toggleReturnedInput(p.id)}
                                      >
                                        {returnedInputEnabled[p.id] ? "Off" : "Enable"}
                                      </Button>
                                      <FormInput
                                        type="number"
                                        min={0}
                                        step={1}
                                        placeholder="0"
                                        className="w-full max-w-20 min-w-0"
                                        value={returnedQuantities[p.id] ?? ""}
                                        onChange={(e) => setReturnedQuantity(p.id, parseInt(e.target.value, 10) || 0)}
                                        disabled={!returnedInputEnabled[p.id]}
                                      />
                                    </div>
                                  </div>
                                  {isOverdrawn && (
                                    <div className="col-span-6 text-xs text-danger text-right py-1">
                                      Net Stock OUT ({(outQty - Number(returnedQuantities[p.id] ?? 0)).toLocaleString()}) exceeds Available Stock ({opening.toLocaleString()}).
                                    </div>
                                  )}
                                </div>
                                  );
                                })()
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                      )}
                    </>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}
